<div class="flex flex-col gap-4">

    <header class="mb-1">
        <h2 class="text-xl font-semibold text-p-text m-0">
            @if (bookingType() === 'reservation') {
                {{ 'bookingV2.summary.titleReservation' | translate }}
            } @else {
                {{ 'bookingV2.summary.titleBooking' | translate }}
            }
        </h2>
        <p class="text-sm text-p-text-muted m-0 mt-1">
            {{ 'bookingV2.summary.subtitle' | translate }}
        </p>
    </header>

    <booking-v2-order-link-card />

    <!-- ─────────────── Person / Organisation ─────────────── -->
    @if (selectedPerson(); as p) {
        <u2b-indicator-card accent="violet" [icon]="p.kind === 'company' ? 'business' : 'person'">
            <div class="flex items-center gap-2 flex-wrap">
                <h3 class="text-sm font-semibold text-p-text m-0">
                    {{ (p.kind === 'company' ? 'bookingV2.summary.company' : 'bookingV2.summary.person') | translate }}
                </h3>
                @if (p.origin === 'new') {
                    <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        {{ 'bookingV2.person.newBadge' | translate }}
                    </span>
                }
            </div>
            <p class="font-medium text-p-text text-sm m-0 mt-0.5">{{ p.displayName }}</p>
            @if (p.contactPerson) {
                <p class="text-sm text-p-text-muted m-0 mt-0.5">
                    {{ 'bookingV2.person.contactPerson' | translate }}: {{ p.contactPerson }}
                </p>
            }
            @if (p.mail || p.phone) {
                <p class="text-sm text-p-text-muted m-0 mt-0.5">
                    @if (p.mail) { {{ p.mail }}@if (p.phone) { · } }
                    @if (p.phone) { {{ p.phone }} }
                </p>
            }
            @if (p.address) {
                <p class="text-xs text-p-text-muted m-0 mt-0.5">{{ p.address }}</p>
            }
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('person')" />
        </u2b-indicator-card>
    }

    <!-- ─────────────── Zeitraum ─────────────── -->
    @if (dateRange().from && (dateRange().to || dateRange().openEnd)) {
        <u2b-indicator-card accent="primary" icon="event">
            <div class="flex flex-col gap-1">
                <h3 class="text-sm font-semibold text-p-text m-0">
                    @if (bookingType() === 'reservation') {
                        {{ 'bookingV2.summary.zeitraumReservation' | translate }}
                    } @else {
                        {{ 'bookingV2.summary.zeitraumBooking' | translate }}
                    }
                </h3>
                <p class="text-sm text-p-text m-0 leading-snug">
                    {{ formatDate(dateRange().from) }}
                    {{ 'bookingV2.dateRange.until' | translate }}
                    @if (dateRange().openEnd) {
                        <span class="inline-flex items-center gap-1 align-middle px-2 py-0.5 rounded-full text-xs font-medium text-p-primary"
                              style="background: color-mix(in srgb, var(--p-primary-color) 14%, transparent);">
                            <span class="material-icons text-[10px] leading-none" aria-hidden="true">all_inclusive</span>
                            {{ 'bookingV2.dateRange.openEndShort' | translate }}
                        </span>
                    } @else {
                        {{ formatDate(dateRange().to) }}
                    }
                </p>
                @if (!dateRange().openEnd && dayCount() > 0) {
                    <p class="text-xs text-p-text-muted m-0">
                        {{ dayCount() }} {{ facade.durationUnitLabelKey() | translate }}
                    </p>
                }
            </div>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('date')" />
        </u2b-indicator-card>
    }

    <!-- ─────────────── Liegeplatzbelegungen ─────────────── -->
    @if (berthOccupancies().length > 0) {
        <u2b-indicator-card accent="teal" icon="anchor">
            <div class="flex flex-col gap-2">
                <h3 class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.summary.berths' | translate }}</h3>
                @for (occ of berthOccupancies(); track occ.uuid) {
                    @if (occ.berth; as berth) {
                        <div>
                            <p class="font-medium text-p-text text-sm m-0">{{ berth.handle }}</p>
                            <p class="text-sm text-p-text-muted m-0">
                                @if (berth.pierHandle) { {{ berth.pierHandle }} · }
                                <booking-v2-dimensions lengthKey="bookingV2.berth.dim.length" [length]="berth.length" [width]="berth.width" [depth]="berth.maxDraft" />
                            </p>
                            <p class="text-sm text-p-text-muted m-0">
                                {{ formatDate(occ.from) }}
                                {{ 'bookingV2.dateRange.until' | translate }}
                                @if (occ.openEnd) {
                                    {{ 'bookingV2.dateRange.openEndShort' | translate }}
                                } @else {
                                    {{ formatDate(occ.to) }}
                                }
                            </p>
                        </div>
                    }
                }
            </div>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('berth')" />
        </u2b-indicator-card>
    }

    <!-- ─────────────── Boot ─────────────── -->
    @if (selectedBoat(); as b) {
        <u2b-indicator-card accent="sky" icon="directions_boat">
            <div class="flex items-center gap-2 flex-wrap">
                <span class="font-medium text-p-text text-sm">
                    @if (b.origin === 'manual') {
                        {{ 'bookingV2.berth.summary.manualBoat' | translate }}
                    } @else {
                        {{ b.name || ('bookingV2.berth.summary.unnamedBoat' | translate) }}
                    }
                </span>
                @if (b.licensePlate) {
                    <span class="text-sm text-p-text-muted">({{ b.licensePlate }})</span>
                }
                @if (b.origin === 'new') {
                    <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                        {{ 'bookingV2.berth.summary.newBadge' | translate }}
                    </span>
                } @else if (b.origin === 'manual') {
                    <span class="text-xs font-medium px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700">
                        {{ 'bookingV2.berth.summary.manualBadge' | translate }}
                    </span>
                }
            </div>
            <p class="text-sm text-p-text-muted m-0 mt-0.5">
                <booking-v2-dimensions [length]="b.length" [width]="b.width" [depth]="b.depth" />
                @if (b.ownerLabel) {
                    · {{ 'bookingV2.berth.summary.ownerLabel' | translate }}: {{ b.ownerLabel }}
                }
            </p>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('berth')" />
        </u2b-indicator-card>
    }

    <!-- ─────────────── Beziehung ─────────────── -->
    @if (selectedRelation()) {
        <u2b-indicator-card accent="primary" icon="sell">
            <div class="flex flex-col gap-2">
                <h3 class="text-sm font-semibold text-p-text m-0">{{ summaryRelationsKey() | translate }}</h3>
                @if (selectedRelation(); as rel) {
                    <div>
                        <div class="flex items-center gap-2">
                            @if (rel.hexColor) {
                                <span class="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" [style.background]="rel.hexColor" aria-hidden="true"></span>
                            }
                            <span class="font-medium text-p-text text-sm">{{ rel.name }}</span>
                        </div>
                        @if (rel.note) {
                            <p class="text-sm text-p-text-muted m-0 mt-0.5">{{ rel.note }}</p>
                        }
                    </div>
                }
            </div>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('person')" />
        </u2b-indicator-card>
    }

    <!-- ─────────────── Notizen ─────────────── -->
    @if (bookingNotes()) {
        <u2b-indicator-card accent="slate" icon="sticky_note_2">
            <div class="flex flex-col gap-1">
                <h3 class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.summary.notes' | translate }}</h3>
                <p class="text-sm text-p-text m-0 whitespace-pre-wrap">{{ bookingNotes() }}</p>
            </div>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToStep('person')" />
        </u2b-indicator-card>
    }

    <!-- ── Vertrag nach dem Speichern (nur Create) ── -->
    @if (!isEditMode()) {
        <u2b-indicator-card accent="teal" icon="description">
            <div class="flex items-center justify-between gap-3">
                <div class="flex flex-col">
                    <span class="text-sm font-semibold text-p-text">{{ 'bookingV2.contract.createAfterSaveLabel' | translate }}</span>
                    <span class="text-xs text-p-text-muted">{{ 'bookingV2.contract.createAfterSaveHint' | translate }}</span>
                </div>
                <p-toggleswitch
                    [ngModel]="contractIntent().create"
                    (ngModelChange)="contractIntent.set({ create: $event })" />
            </div>
        </u2b-indicator-card>
    }

    <!-- ── Erweitert: Reisende / Strom / Schlüssel / Zus. Leistungen / Dokumente (read-only) ── -->
    @if (hasAdvancedData()) {
        <u2b-indicator-card accent="amber" icon="tune">
            <h3 class="text-sm font-semibold text-p-text m-0">{{ 'bookingV2.advanced.title' | translate }}</h3>
            <div class="mt-3">
                <booking-v2-advanced-step [readOnly]="true" />
            </div>
            <u2b-icon-button u2bIndicatorAction icon="edit" [ariaLabel]="('common.edit' | translate)"
                [tooltip]="('common.edit' | translate)" tooltipPosition="top"
                (clicked)="facade.goToAdvancedStep()" />
        </u2b-indicator-card>
    }

</div>
